import type * as React from "react";

import { cn } from "../lib/cn";

/** Density. `md` is the default control; `sm` is the compact key on a control bar. */
export type SegmentedControlSize = "sm" | "md";

/**
 * What the unselected segments are made of. `outline` is card stock with a
 * hairline edge (a row of little paper chips); `ghost` has no resting surface
 * at all, for a segment set that lives inside a band of other controls.
 */
export type SegmentedControlVariant = "outline" | "ghost";

const sizeClasses: Record<SegmentedControlSize, string> = {
  sm: "px-2 py-1 text-xs",
  md: "px-2.5 py-1 text-sm",
};

/*
 * The chosen segment is the pressed key in both variants: full ink, paper
 * showing through the letters. Only the resting state tells the variants apart.
 *
 * `outline` keeps its border in the chosen state too - the same color as the
 * fill, so it is invisible - because a border that appears and disappears with
 * selection changes the segment's width by 2px and shoves its neighbours along
 * the row.
 */
const variantClasses: Record<
  SegmentedControlVariant,
  { chosen: string; resting: string; hover: string }
> = {
  outline: {
    chosen: "border border-ink bg-ink text-ink-inverse",
    resting: "border border-rule bg-card text-ink",
    /* Paper lifts, it does not recolor. */
    hover: "hover:-translate-y-px hover:shadow-lift-lg",
  },
  ghost: {
    chosen: "bg-ink text-ink-inverse",
    resting: "text-annotation",
    /* No resting surface to lift, so ghost picks up an ink wash instead -
       the same exception Button's ghost variant makes. */
    hover: "hover:bg-smudge hover:text-ink",
  },
};

export interface SegmentedControlOption<T extends string | number = string> {
  value: T;
  /** The word on the key. Keep it to a word or two - this is not a menu. */
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<
  T extends string | number = string,
> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "role" | "onChange" | "children" | "aria-label"
> {
  /** Two to four of them. More than that is a `Select`. */
  options: readonly SegmentedControlOption<T>[];
  /** The chosen value. Controlled; a value matching no option chooses nothing. */
  value: T;
  onValueChange: (value: T) => void;
  size?: SegmentedControlSize;
  variant?: SegmentedControlVariant;
  /** Figures (speeds, counts, prices) set in the mono face with tabular figures. */
  mono?: boolean;
  /**
   * Names the group - required. A radio group with no name is announced as a
   * bare "group", and the segments then read as unrelated buttons. Where a
   * visible heading already names the choice, pass its words.
   */
  "aria-label": string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A row of segments for one exclusive choice among two to four: the view a
 * calendar is showing, the speed a replay is running at. Choosing is immediate
 * and reversible - a segmented control changes a setting, it never submits.
 *
 * It is a `radiogroup`, not a row of `aria-pressed` buttons. Pressed-state
 * buttons say "Week, pressed" and nothing about the other two: no group, no
 * "1 of 3", and nothing telling a screen reader that choosing one un-chooses
 * the rest. That reading is right for an independent toggle (a filter chip that
 * can be on while its neighbours are on) and wrong for a setting with exactly
 * one answer, which is what every segment set here is. Keep `aria-pressed`
 * chips for the multi-select case.
 *
 * Keyboard is the APG radio group: one tab stop for the whole row, arrows move
 * *and* choose (wrapping, skipping disabled), Home/End jump to the ends, Space
 * and Enter choose the focused segment. Roving tabindex is implemented here
 * rather than through `useListboxNav` - that hook is an active-descendant
 * controller with typeahead and vertical arrows, and decouples movement from
 * selection, which is three things a radio group does differently.
 *
 * Native props land on the group element. `role` and the key handler are not
 * among them: they are the widget.
 */
export function SegmentedControl<T extends string | number = string>({
  options,
  value,
  onValueChange,
  size = "md",
  variant = "outline",
  mono = false,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  const chosenIndex = options.findIndex((option) => option.value === value);
  /* The chosen segment holds the row's one tab stop, but only if it can take
     it: a disabled button is not focusable, so parking tabIndex 0 there drops
     the whole radiogroup out of the tab order just as surely as having nothing
     chosen at all. Both cases fall back to the first enabled segment. */
  const chosenOption = chosenIndex === -1 ? undefined : options[chosenIndex];
  const tabStopIndex =
    chosenOption && !chosenOption.disabled
      ? chosenIndex
      : options.findIndex((option) => !option.disabled);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "ArrowUp" &&
      event.key !== "ArrowDown" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    const segments = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="radio"]:not([disabled])',
      ),
    );
    if (segments.length === 0) return;

    const current = segments.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    let nextIndex: number;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = current <= 0 ? segments.length - 1 : current - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = current === segments.length - 1 ? 0 : current + 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = segments.length - 1;
    }

    const next = segments[nextIndex];
    if (!next) return;

    event.preventDefault();
    next.focus();
    /* Selection follows focus: in a radio group, moving is choosing. The index
       is carried on the element because option values may be numbers. */
    const option = options[Number(next.dataset.index)];
    if (option) onValueChange(option.value);
  }

  return (
    <div
      {...rest}
      role="radiogroup"
      onKeyDown={handleKeyDown}
      className={cn("inline-flex items-center gap-1", className)}
    >
      {options.map((option, index) => {
        const chosen = index === chosenIndex;
        const chrome = variantClasses[variant];
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            data-index={index}
            tabIndex={index === tabStopIndex ? 0 : -1}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "cursor-pointer select-none rounded-paper font-body font-medium",
              "transition duration-150 ease-paper motion-reduce:transition-none",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              "disabled:cursor-not-allowed disabled:text-faint",
              sizeClasses[size],
              mono && "font-mono font-normal tabular-nums",
              chosen ? chrome.chosen : chrome.resting,
              !option.disabled && chrome.hover,
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
